import React, { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Layout from './components/Layout';
import DocumentAnalysis from './pages/DocumentAnalysis';
import DocumentScans from './pages/DocumentScans';
import DatabaseViewer from './pages/DatabaseViewer';
import Settings from './pages/Settings';
// NOTE: CreateNewLoan functionality deprecated - creates loans via case list now
// import CreateNewLoan from './pages/CreateNewLoan';
import SystemFiles from './pages/SystemFiles';
import CaseHistory from './pages/CaseHistory';
import Tasks from './pages/Tasks';
import Admin from './pages/Admin';
import UserManagement from './pages/UserManagement';
import ResultsTest from './test/ResultsTest';
import TestWebSockets from './pages/TestWebSockets';
import LoginPage from './components/auth/LoginPage';
import SsoFinish from './pages/SsoFinish';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ToastContainer from './components/Toast';
import { removeNotification, addNotification } from './store/slices/uiSlice';
import { setExtractionResultsFromWebSocket } from './store/slices/documentsSlice';
import { updateFromServer as updateProgressFromServer, complete as completeProgress } from './store/slices/progressSlice';
import socketService from './services/socketService';


function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const notifications = useSelector(state => state.ui.notifications);
  const { extractionResults } = useSelector(state => state.documents);
  const targetSocketId = useSelector(state => state.progress.targetSocketId);
  
  // Extract loanId from URL parameters
  const extractLoanIdFromPath = () => {
    const match = location.pathname.match(/\/(document-analysis|system-files|history)\/([^\/]+)/);
    return match ? match[2] : null;
  };
  
  const loanId = extractLoanIdFromPath();

  // Set up WebSocket listener for extraction results
  useEffect(() => {
    const handleExtractionResults = (results) => {
      if (results && results.socket_id && targetSocketId && results.socket_id !== targetSocketId) {
        console.log('📊 [App] Ignoring extraction results for different socket:', results.socket_id);
        return;
      }
      console.log('📊 App received extraction results via WebSocket:', results);
      console.log('🔍 WebSocket Results - Total fields:', Object.keys(results).filter(k => k !== '_metadata').length);
      console.log('🔍 WebSocket Results - Margin field:', results['Margin']);
      console.log('🔍 WebSocket Results - Max Rate field:', results['Max Rate']);
      console.log('🔍 WebSocket Results - Min Rate field:', results['Min Rate']);
      dispatch(setExtractionResultsFromWebSocket(results));
      dispatch(completeProgress(results));
      
      // Show success notification when results arrive
      dispatch(addNotification({
        type: 'success',
        message: `🎉 Extraction completed! ${Object.keys(results).filter(k => k !== '_metadata').length} fields processed.`,
        duration: 4000
      }));
    };

    // Connect to Socket.IO and subscribe to extraction results
    socketService.subscribe('extraction_results', handleExtractionResults);

    // Cleanup on unmount
    return () => {
      socketService.unsubscribe('extraction_results', handleExtractionResults);
    };
  }, [dispatch, targetSocketId]);

  // Set up persistent WebSocket listeners for progress updates (stays mounted for entire app lifetime)
  useEffect(() => {
    // console.log('🔔 [App] Setting up persistent socket listeners for progress updates');
    
    const handleProgressUpdate = (data) => {
      console.log('🔔 [App] Dispatching progress_update to Redux:', data);
      dispatch(updateProgressFromServer(data));
    };

    const handleProgressComplete = (data) => {
      console.log('🔔 [App] Dispatching progress_complete to Redux:', data);
      dispatch(completeProgress(data));
    };

    socketService.subscribe('progress_update', handleProgressUpdate);
    socketService.subscribe('progress_complete', handleProgressComplete);

    return () => {
      socketService.unsubscribe('progress_update', handleProgressUpdate);
      socketService.unsubscribe('progress_complete', handleProgressComplete);
    };
  }, [dispatch]);

  const handleRemoveNotification = (notificationId) => {
    dispatch(removeNotification(notificationId));
  };

  // Calculate metrics from extraction results
  const calculateMetrics = () => {
    if (!extractionResults || Object.keys(extractionResults).length === 0) {
      return {
        totalFields: 0,
        highMatches: 0,
        discrepancies: 0,
        matchRate: 0
      };
    }

    const fields = Object.entries(extractionResults).filter(([key]) => key !== '_metadata');
    const totalFields = fields.length;
    
    let highMatches = 0;
    let discrepancies = 0;
    
    fields.forEach(([key, field]) => {
      if (!field) return; // Skip null/undefined fields
      
      if (field.system_document_match === 'HIGH') {
        highMatches++;
      }
      if (field.value !== field.master_value && field.value !== 'Not Found' && field.master_value !== 'Not Found') {
        discrepancies++;
      }
    });

    const matchRate = totalFields > 0 ? Math.round((highMatches / totalFields) * 100) : 0;

    return {
      totalFields,
      highMatches,
      discrepancies,
      matchRate
    };
  };

  const metrics = calculateMetrics();
  const loanData = { extractionResults, metrics };

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/sso/finish" element={<SsoFinish />} />
        
        {/* Protected routes with Layout */}
        <Route path="/*" element={
          <ProtectedRoute>
            <Layout loanId={loanId} loanData={loanData}>
              <Routes>
                <Route path="/" element={<Navigate to="/case-list" replace />} />
                <Route path="/document-analysis/:loanId/scans" element={<DocumentScans />} />
                <Route path="/document-analysis/:loanId/documents" element={<DocumentAnalysis />} />
                <Route path="/document-analysis/:loanId" element={<DocumentAnalysis />} />
                <Route path="/document-analysis" element={<DocumentAnalysis />} />
                {/* NOTE: CreateNewLoan route deprecated - use case list to create loans */}
                {/* <Route path="/create" element={<CreateNewLoan />} /> */}
                <Route path="/case-list" element={<DatabaseViewer />} />
                <Route path="/database" element={<DatabaseViewer />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/system-files/:loanId" element={<SystemFiles />} />
                <Route path="/system-files" element={<SystemFiles />} />
                <Route path="/history/:loanId" element={<CaseHistory />} />
                <Route path="/admin" element={
                  <ProtectedRoute requiredRole="super_admin">
                    <Admin />
                  </ProtectedRoute>
                } />
                <Route path="/users" element={
                  <ProtectedRoute requiredRole="admin">
                    <UserManagement />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={<Settings />} />
                <Route path="/test" element={<ResultsTest />} />
                <Route path="/test-websockets" element={<TestWebSockets />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        } />
      </Routes>
      <ToastContainer 
        notifications={notifications} 
        onRemove={handleRemoveNotification} 
      />
    </>
  );
}

export default App;
